import type { ConditionField, PromotedFiltersProps } from '@pega/cosmos-react-condition-builder';
import { createStringMatcher } from '@pega/cosmos-react-core';

export const demoCondition: PromotedFiltersProps['condition'] = {
  AND: [
    {
      condition: {
        lhs: {
          field: 'FirstName'
        },
        comparator: 'EQ',
        rhs: {
          value: 'John'
        }
      }
    },
    {
      condition: {
        lhs: { field: 'RemoteEmployee' },
        comparator: 'IS_TRUE'
      }
    }
  ]
};

export const demoFilters: PromotedFiltersProps['filters'] = [
  { field: { name: 'FirstName', type: 'TEXT' }, mode: 'default', label: 'First name' },
  {
    id: 'anotherFirstName',
    field: { name: 'FirstName', type: 'TEXT' },
    mode: 'advanced',
    label: 'New first name'
  },
  { field: { name: 'LastName', type: 'TEXT' }, mode: 'advanced', label: 'Last name' },
  {
    field: {
      name: 'JobDescription',
      type: 'TEXT',
      possibleValues: [
        'Software Engineer',
        'Front End Developer',
        'QA Engineer',
        'Manager',
        'Product Manager',
        'Product Owner'
      ]
    },
    mode: 'advanced',
    label: 'Job description'
  },
  {
    field: { name: 'Experience', type: 'INTEGER' },
    mode: 'default',
    label: 'Years of experience'
  },
  {
    field: {
      name: 'Score',
      type: 'INTEGER'
    },
    mode: 'predefined',
    label: 'Score',
    predefinedConditions: [
      {
        id: 'score1',
        value: {
          lhs: {
            field: 'Score'
          },
          comparator: 'GT',
          rhs: {
            value: 0
          }
        }
      },
      {
        id: 'score2',
        value: {
          lhs: {
            field: 'Score'
          },
          comparator: 'EQ',
          rhs: {
            value: 0
          }
        }
      },
      {
        id: 'score3',
        value: {
          lhs: {
            field: 'Score'
          },
          comparator: 'LT',
          rhs: {
            value: 0
          }
        }
      },
      {
        id: 'score4',
        value: {
          lhs: {
            field: 'Score'
          },
          comparator: 'BTW',
          rhs: {
            start: 0,
            end: 10
          }
        }
      },
      {
        id: 'score5',
        value: {
          lhs: {
            field: 'Score'
          },
          comparator: 'IS_NULL'
        }
      }
    ]
  },
  {
    field: {
      name: 'Hired',
      type: 'DATE_ONLY'
    },
    mode: 'predefined',
    label: 'Hired',
    predefinedConditions: [
      {
        id: 'hired1',
        value: {
          lhs: {
            field: 'Hired'
          },
          comparator: 'LT',
          rhs: {
            value: '2022-01-01'
          }
        },
        label: 'Before 2022'
      },
      {
        id: 'hired2',
        value: {
          lhs: {
            field: 'Hired'
          },
          comparator: 'GT',
          rhs: {
            value: '2022-01-01'
          }
        },
        label: 'After 2022'
      },
      {
        id: 'hired3',
        value: {
          lhs: {
            field: 'Hired'
          },
          comparator: 'BTW',
          rhs: {
            start: '2023-01-03',
            end: '2023-31-03'
          }
        },
        label: 'In March 2023'
      }
    ]
  },
  {
    field: { name: 'Salary', type: 'INTEGER' },
    mode: 'advanced',
    label: 'Salary'
  },
  {
    field: { name: 'HireDate', type: 'DATE_ONLY' },
    mode: 'default',
    label: 'Hire date'
  },
  {
    field: { name: 'LastPromotion', type: 'DATE_ONLY' },
    mode: 'advanced',
    label: 'Date of last promotion'
  },
  {
    field: { name: 'DailyMeetingTime', type: 'TIME_ONLY' },
    mode: 'default',
    label: 'Time of daily meeting'
  },
  {
    field: { name: 'StartOfWorkingHours', type: 'TIME_ONLY' },
    mode: 'advanced',
    label: 'Start of working hours'
  },
  {
    field: { name: 'NextReviewDate', type: 'DATE_TIME' },
    mode: 'default',
    label: 'Next scheduled review'
  },
  {
    field: { name: 'LastOfficeCheckIn', type: 'DATE_TIME' },
    mode: 'advanced',
    label: 'Last check-in in the office'
  },
  {
    field: { name: 'RemoteEmployee', type: 'BOOLEAN' },
    mode: 'default',
    label: 'Remote employee'
  },
  {
    field: {
      name: 'Department',
      type: 'PICKLIST',
      possibleValues: [
        'Customer Service',
        'Engineering',
        'Facilities',
        'Finance',
        'HR',
        'IT',
        'Sales'
      ]
    },
    mode: 'default'
  },
  {
    field: {
      name: 'Status',
      type: 'PICKLIST',
      possibleValues: args => {
        const values = [
          { id: 'new', primary: 'New' },
          { id: 'open', primary: 'Open' },
          { id: 'resolved', primary: 'Resolved' },
          { id: 'new1', primary: 'New 1' },
          { id: 'open1', primary: 'Open 1' },
          { id: 'resolved1', primary: 'Resolved 1' },
          { id: 'new2', primary: 'New 2' },
          { id: 'open2', primary: 'Open 2' },
          { id: 'resolved2', primary: 'Resolved 2' },
          { id: 'new3', primary: 'New 3' },
          { id: 'open3', primary: 'Open 3' },
          { id: 'resolved3', primary: 'Resolved 3' },
          { id: 'new4', primary: 'New 4' },
          { id: 'open4', primary: 'Open 4' },
          { id: 'resolved4', primary: 'Resolved 4' },
          { id: 'new5', primary: 'New 5' },
          { id: 'open5', primary: 'Open 5' },
          { id: 'resolved5', primary: 'Resolved 5' },
          { id: 'new6', primary: 'New 6' },
          { id: 'open6', primary: 'Open 6' },
          { id: 'resolved6', primary: 'Resolved 6' }
        ];

        function isFetchingSelectedValues(
          arg: typeof args
        ): arg is { lhs?: ConditionField; ids: string[] } {
          return 'ids' in arg;
        }

        if (isFetchingSelectedValues(args)) {
          return Promise.resolve(
            values.filter(({ id }) => {
              return args.ids && args.ids.includes(id);
            })
          );
        }
        const start = args.pageNumber && args.pageSize ? (args.pageNumber - 1) * args.pageSize : 0;
        return new Promise(resolve => {
          setTimeout(() => {
            resolve(
              values
                .filter(({ primary }) => {
                  return createStringMatcher(args.searchString ?? '').test(primary);
                })
                .slice(start, args.pageSize ? start + args.pageSize : undefined)
            );
          }, 100);
        });
      }
    },
    mode: 'advanced'
  }
];
